
import { COUNTRY_CODES } from './constants';

// --- Helpers ---
export const sanitizePersonName = (val: string) => val.replace(/[^a-zA-Z\s]/g, '').slice(0, 30);
export const sanitizeEntityName = (val: string) => val.slice(0, 50);
export const sanitizePhoneNumber = (val: string) => val.replace(/[^0-9]/g, '').slice(0, 15);

export interface DetectedCountry {
    name: string;
    code: string;
    flag: string;
    minLen: number;
    maxLen: number;
    iso?: string;
}

/**
 * Auto-detects the user's country without permissions using standard browser
 * Timezone and Locale APIs. Returns the matching country object from COUNTRY_CODES.
 */
export const detectUserCountry = (): DetectedCountry => {
    try {
        // 1. Timezone-based detection (most accurate and permission-free)
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
        
        // India
        if (timeZone.includes('Kolkata') || timeZone.includes('Calcutta') || timeZone.includes('Colombo')) {
            const found = COUNTRY_CODES.find(c => c.name === 'India');
            if (found) return found;
        }
        // Canada vs US check (both +1)
        if (timeZone.startsWith('Canada/') || [
            'America/Toronto', 'America/Vancouver', 'America/Edmonton', 
            'America/Winnipeg', 'America/Halifax', 'America/St_Johns', 
            'America/Montreal', 'America/Regina', 'America/Moncton',
            'America/Dawson', 'America/Whitehorse', 'America/Yellowknife'
        ].includes(timeZone)) {
            const found = COUNTRY_CODES.find(c => c.name === 'Canada');
            if (found) return found;
        }
        // United States
        if (timeZone.startsWith('US/') || timeZone.startsWith('America/')) {
            const found = COUNTRY_CODES.find(c => c.name === 'United States');
            if (found) return found;
        }
        // United Kingdom
        if (timeZone === 'Europe/London' || timeZone === 'Europe/Belfast' || timeZone === 'GB') {
            const found = COUNTRY_CODES.find(c => c.name === 'United Kingdom');
            if (found) return found;
        }
        // Australia
        if (timeZone.startsWith('Australia/') || ['Antarctica/Macquarie'].includes(timeZone)) {
            const found = COUNTRY_CODES.find(c => c.name === 'Australia');
            if (found) return found;
        }
        // Germany
        if (timeZone === 'Europe/Berlin' || timeZone === 'Europe/Busingen') {
            const found = COUNTRY_CODES.find(c => c.name === 'Germany');
            if (found) return found;
        }
        // France
        if (timeZone === 'Europe/Paris') {
            const found = COUNTRY_CODES.find(c => c.name === 'France');
            if (found) return found;
        }
        // Japan
        if (timeZone === 'Asia/Tokyo') {
            const found = COUNTRY_CODES.find(c => c.name === 'Japan');
            if (found) return found;
        }

        // 2. Locale / Language detection fallback
        const languages = (typeof navigator !== 'undefined' ? (navigator.languages || [navigator.language]) : []) as string[];
        for (const lang of languages) {
            if (!lang) continue;
            const lower = lang.toLowerCase();
            if (lower.endsWith('-in') || ['hi', 'ta', 'te', 'mr', 'gu', 'kn', 'ml', 'bn', 'pa'].some(p => lower.startsWith(p))) {
                const found = COUNTRY_CODES.find(c => c.name === 'India');
                if (found) return found;
            }
            if (lower.endsWith('-gb') || lower === 'en-uk') {
                const found = COUNTRY_CODES.find(c => c.name === 'United Kingdom');
                if (found) return found;
            }
            if (lower.endsWith('-ca')) {
                const found = COUNTRY_CODES.find(c => c.name === 'Canada');
                if (found) return found;
            }
            if (lower.endsWith('-au')) {
                const found = COUNTRY_CODES.find(c => c.name === 'Australia');
                if (found) return found;
            }
            if (lower.endsWith('-de') || lower.startsWith('de')) {
                const found = COUNTRY_CODES.find(c => c.name === 'Germany');
                if (found) return found;
            }
            if (lower.endsWith('-fr') || lower.startsWith('fr')) {
                const found = COUNTRY_CODES.find(c => c.name === 'France');
                if (found) return found;
            }
            if (lower.endsWith('-jp') || lower.startsWith('ja')) {
                const found = COUNTRY_CODES.find(c => c.name === 'Japan');
                if (found) return found;
            }
            if (lower.endsWith('-us')) {
                const found = COUNTRY_CODES.find(c => c.name === 'United States');
                if (found) return found;
            }
        }
    } catch (err) {
        console.warn("Country detection fallback to default:", err);
    }

    // Default fallback (United States)
    return COUNTRY_CODES[0];
};

export const getCountryFromPhone = (phone?: string): DetectedCountry | null => {
    if (!phone) return null;
    return COUNTRY_CODES
        .slice()
        .sort((a, b) => b.code.length - a.code.length)
        .find(c => phone.startsWith(c.code)) || null;
};

export const getCountryNameFromPhone = (phone?: string): string => {
    const match = getCountryFromPhone(phone);
    return match ? match.name : '';
};

export const getDialingCodeForCountry = (countryName?: string): string => {
    if (!countryName) return '';
    const match = COUNTRY_CODES.find(c => c.name.toLowerCase() === countryName.toLowerCase());
    return match ? match.code : '';
};

export const validatePhoneNumber = (fullNumber: string): boolean => {
    if (!fullNumber) return false;
    // Find the country code (longest match first)
    const country = COUNTRY_CODES
        .sort((a, b) => b.code.length - a.code.length)
        .find(c => fullNumber.startsWith(c.code));
    
    if (!country) return false;
    
    const numberPart = fullNumber.slice(country.code.length);
    return numberPart.length >= country.minLen && numberPart.length <= country.maxLen;
};

export const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};

/**
 * Automatically resizes and compresses an image (File or base64 data URL)
 * using HTML5 Canvas. Produces an ultra-lightweight JPEG (< 40KB) that
 * prevents Firestore payload limits and speeds up network requests.
 */
export const compressImage = (
  fileOrDataUrl: File | string,
  maxDimension = 512,
  quality = 0.8
): Promise<string> => {
  return new Promise((resolve) => {
    if (!fileOrDataUrl) {
      resolve('');
      return;
    }

    const processSrc = (src: string) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          } else {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(width, 1);
        canvas.height = Math.max(height, 1);
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', quality));
        } else {
          resolve(src);
        }
      };
      img.onerror = () => resolve(src);
      img.src = src;
    };

    if (typeof fileOrDataUrl === 'string') {
      processSrc(fileOrDataUrl);
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        if (result) processSrc(result);
        else resolve('');
      };
      reader.onerror = () => resolve('');
      reader.readAsDataURL(fileOrDataUrl);
    }
  });
};

/**
 * Determines file attachment type from URL or data URL.
 */
export const getAttachmentType = (url: string): 'image' | 'pdf' | 'doc' | 'file' => {
  if (!url) return 'file';
  const cleanUrl = url.split('?')[0].toLowerCase();
  if (cleanUrl.match(/\.(jpeg|jpg|png|webp|gif)$/) || url.startsWith('data:image/')) {
    return 'image';
  }
  if (cleanUrl.endsWith('.pdf') || url.startsWith('data:application/pdf')) {
    return 'pdf';
  }
  if (cleanUrl.endsWith('.doc') || cleanUrl.endsWith('.docx') || cleanUrl.includes('wordprocessingml')) {
    return 'doc';
  }
  return 'file';
};

/**
 * Extracts a human-friendly display name from an attachment URL.
 */
export const getAttachmentFileName = (url: string): string => {
  if (!url) return 'Attachment';
  if (url.startsWith('data:')) {
    if (url.startsWith('data:image/')) return 'Photo Attachment';
    if (url.startsWith('data:application/pdf')) return 'PDF Document';
    return 'Attached File';
  }
  try {
    const withoutQuery = url.split('?')[0];
    const parts = withoutQuery.split('/');
    const lastPart = parts[parts.length - 1] || 'Attachment';
    const decoded = decodeURIComponent(lastPart);
    // Strip timestamp & random id prefix (e.g. 1712345678_abcd_filename.pdf)
    const cleaned = decoded.replace(/^\d+_[a-zA-Z0-9]+_/, '');
    return cleaned || 'Attachment';
  } catch {
    return 'Attachment';
  }
};

